// askSmart-ui/src/components/SMRTDocsSignersModal.tsx
import React, { useEffect, useState } from "react";
import { useUsersWithModuleAccess } from "../hooks/useUsersWithModuleAccess";

interface SignerRow {
  userId: string;
  name: string;
  email: string;
}

export default function SMRTDocsSignersModal({
  isOpen,
  onClose,
  onSubmit,
}: any) {
  // Pull the org's user list filtered by SMRT Docs RBAC access. Hook
  // joins /approved_users with /roles?scope=org and keeps only users
  // whose roles grant the smrtdocs view/entitlement keys (or Owner).
  const users = useUsersWithModuleAccess("/smrt-docs");
  const [signers, setSigners] = useState<SignerRow[]>([
    { userId: "", name: "", email: "" },
  ]);

  useEffect(() => {
    if (!isOpen) return;
    setSigners([{ userId: "", name: "", email: "" }]);
  }, [isOpen]);

  if (!isOpen) return null;

  const updateSigner = (index: number, userId: string) => {
    const u = users.find((x) => x.id === userId);
    const updated = [...signers];
    updated[index] = {
      userId,
      name: u?.name || "",
      email: u?.email || "",
    };
    setSigners(updated);
  };

  const addSigner = () => {
    setSigners([...signers, { userId: "", name: "", email: "" }]);
  };

  const removeSigner = (index: number) => {
    if (signers.length <= 1) return;
    setSigners(signers.filter((_, i) => i !== index));
  };

  const moveUp = (index: number) => {
    if (index === 0) return;
    const updated = [...signers];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    setSigners(updated);
  };

  const moveDown = (index: number) => {
    if (index === signers.length - 1) return;
    const updated = [...signers];
    [updated[index + 1], updated[index]] = [updated[index], updated[index + 1]];
    setSigners(updated);
  };

  const handleSubmit = () => {
    if (signers.some((s) => !s.userId)) {
      alert("Each signer must be a selected user");
      return;
    }
    const ids = signers.map((s) => s.userId);
    if (new Set(ids).size !== ids.length) {
      alert("Each signer must be a distinct user");
      return;
    }
    // Any number of signers (1, 2, 3, …) is allowed. countersignRequired
    // is still accepted as a prop so existing callers don't break, but
    // it no longer caps the roster size.
    if (signers.length < 1) {
      alert("At least one signer required");
      return;
    }
    onSubmit(signers);
  };

  return (
    <div className="smrtdocs-modal-overlay">
      <div className="smrtdocs-modal" style={{ width: "700px" }}>
        <div className="smrtdocs-modal-header">
          <h2>Signers</h2>
          <button className="btn-cancel" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="smrtdocs-modal-body">
          {signers.map((s, i) => (
            <div
              key={i}
              style={{
                display: "grid",
                gridTemplateColumns: "4fr 1fr",
                gap: "8px",
                marginBottom: "8px",
              }}
            >
              <select
                className="manage-input"
                value={s.userId}
                onChange={(e) => updateSigner(i, e.target.value)}
              >
                <option value="">Select user…</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>

              <div
                style={{ display: "flex", gap: "6px", alignItems: "center" }}
              >
                <button
                  className="btn-icon"
                  title="Move Up"
                  onClick={() => moveUp(i)}
                >
                  ↑
                </button>

                <button
                  className="btn-icon"
                  title="Move Down"
                  onClick={() => moveDown(i)}
                >
                  ↓
                </button>

                <button
                  className="btn-icon btn-toggle-delete"
                  title="Remove"
                  onClick={() => removeSigner(i)}
                >
                  🗑
                </button>
              </div>
            </div>
          ))}

          <button className="btn-add" onClick={addSigner}>
            + Add Signer
          </button>
        </div>

        <div style={{ padding: "10px", textAlign: "right" }}>
          <button className="btn-save" onClick={handleSubmit}>
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
